/**
 * SOLOMON — UNDO BANNER
 * Full-width banner shown after an action is approved.
 * 60-second countdown with one-tap undo button.
 *
 * Variants:
 *   - reversible:   warm red glass background with undo button + countdown
 *   - irreversible: amber glass background with "cannot be undone" message
 *
 * Auto-dismisses when countdown reaches zero.
 */

import { useState, useEffect, useCallback } from 'react';
import './UndoBanner.css';

export default function UndoBanner({
  actionId,
  actionType,
  reversible = true,
  undoDeadline,
  onUndo,
  onDismiss,
}) {
  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (!undoDeadline) return 60;
    return Math.max(0, Math.ceil((new Date(undoDeadline) - Date.now()) / 1000));
  });
  const [undoing, setUndoing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (!reversible || secondsLeft <= 0) return;

    const timer = setInterval(() => {
      setSecondsLeft(prev => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(timer);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [reversible, secondsLeft > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss when countdown reaches 0
  useEffect(() => {
    if (secondsLeft === 0 && reversible) {
      const timeout = setTimeout(() => {
        setDismissed(true);
        onDismiss?.();
      }, 1500); // Show "Window closed" briefly
      return () => clearTimeout(timeout);
    }
  }, [secondsLeft, reversible, onDismiss]);

  const handleUndo = useCallback(async () => {
    if (undoing || secondsLeft <= 0) return;
    setUndoing(true);
    try {
      await onUndo?.(actionId);
      setDismissed(true);
    } catch {
      setUndoing(false);
    }
  }, [actionId, onUndo, undoing, secondsLeft]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    onDismiss?.();
  }, [onDismiss]);

  if (dismissed) return null;

  const typeLabel = actionType?.replace(/_/g, ' ') || 'Action';
  const progressPct = reversible ? (secondsLeft / 60) * 100 : 0;
  const expired = reversible && secondsLeft <= 0;

  return (
    <div className={`undo-banner ${reversible ? 'undo-banner--reversible' : 'undo-banner--irreversible'} ${expired ? 'undo-banner--expired' : ''}`}>

      {/* Progress bar (reversible only) */}
      {reversible && !expired && (
        <div className="undo-banner-progress">
          <div
            className="undo-banner-progress-bar"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      <div className="undo-banner-content">
        {/* Status text */}
        <div className="undo-banner-text">
          {expired ? (
            <span className="undo-banner-expired-text">Undo window closed</span>
          ) : reversible ? (
            <>
              <span className="undo-banner-label">{typeLabel} executed.</span>
              <span className="undo-banner-countdown">{secondsLeft}s to undo</span>
            </>
          ) : (
            <>
              <span className="undo-banner-label">{typeLabel} executed.</span>
              <span className="undo-banner-irreversible-text">This action cannot be undone.</span>
            </>
          )}
        </div>

        {/* Buttons */}
        <div className="undo-banner-actions">
          {reversible && !expired && (
            <button
              className="undo-banner-undo-btn"
              onClick={handleUndo}
              disabled={undoing}
            >
              {undoing ? 'Undoing...' : 'Undo'}
            </button>
          )}
          <button
            className="undo-banner-dismiss-btn"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            &#10005;
          </button>
        </div>
      </div>
    </div>
  );
}
